// Merkan · Passkeys (Face ID, Touch ID, Fingerabdruck, Windows Hello) als freiwilliger zweiter Faktor
// Die Prüfung der Signaturen übernimmt die Edge Function "passkey".
import { browserSupportsWebAuthn, startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { supabase } from './supabase';
import { t } from './sprache';

export interface Passkey {
  id: string;
  name: string;
  rp_id: string;
  geraet_typ: string | null;
  gesichert: boolean | null;
  erstellt_am: string;
  zuletzt_genutzt: string | null;
}

export type PasskeyErgebnis = { ok: true; name?: string } | { ok: false; fehler: string; abgebrochen?: boolean; code?: string };

export function passkeysMoeglich(): boolean {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

/** RP-ID der aktuellen Adresse, wie sie die Edge Function vergibt */
export function aktuelleRpId(): string {
  const h = window.location.hostname;
  return /(^|\.|-)merkan\.parlinex\.io$/.test(h) ? 'parlinex.io' : h;
}

async function aufruf(aktion: string, daten: Record<string, unknown> = {}): Promise<{ ok?: boolean; fehler?: string; code?: string; optionen?: unknown; name?: string }> {
  const { data, error } = await supabase.functions.invoke('passkey', { body: { aktion, ...daten } });
  if (error) {
    const ctx = (error as { context?: Response }).context;
    try {
      const j = ctx ? await ctx.json() : null;
      if (j?.fehler) return j;
    } catch {
      /* kein JSON-Körper */
    }
    return { fehler: t('Der Dienst für Passkeys ist gerade nicht erreichbar. Bitte später erneut versuchen.') };
  }
  return data ?? {};
}

/** Abbruch durch den Benutzer (Dialog geschlossen, Face ID abgebrochen) erkennen */
function istAbbruch(e: unknown): boolean {
  const name = (e as { name?: string })?.name ?? '';
  return name === 'NotAllowedError' || name === 'AbortError';
}

function browserFehler(e: unknown): string {
  const name = (e as { name?: string })?.name ?? '';
  if (name === 'InvalidStateError') return t('Dieser Passkey ist auf dem Gerät bereits hinterlegt.');
  if (name === 'SecurityError') return t('Passkeys sind unter dieser Adresse nicht möglich.');
  return t('Der Vorgang ist fehlgeschlagen. Bitte erneut versuchen.');
}

export async function passkeyHinzufuegen(name: string): Promise<PasskeyErgebnis> {
  const start = await aufruf('registrieren_start');
  if (!start.ok || !start.optionen) return { ok: false, fehler: start.fehler ?? t('Der Vorgang ist fehlgeschlagen. Bitte erneut versuchen.'), code: start.code };
  let antwort;
  try {
    antwort = await startRegistration({ optionsJSON: start.optionen as never });
  } catch (e) {
    return istAbbruch(e) ? { ok: false, fehler: '', abgebrochen: true } : { ok: false, fehler: browserFehler(e) };
  }
  const ende = await aufruf('registrieren_ende', { antwort, name });
  return ende.ok ? { ok: true, name: ende.name } : { ok: false, fehler: ende.fehler ?? t('Der Passkey konnte nicht gespeichert werden.') };
}

/** Aktuelle Sitzung mit Passkey bestätigen */
export async function mitPasskeyBestaetigen(): Promise<PasskeyErgebnis> {
  const start = await aufruf('anmelden_start');
  if (!start.ok || !start.optionen) return { ok: false, fehler: start.fehler ?? t('Der Vorgang ist fehlgeschlagen. Bitte erneut versuchen.'), code: start.code };
  let antwort;
  try {
    antwort = await startAuthentication({ optionsJSON: start.optionen as never });
  } catch (e) {
    return istAbbruch(e) ? { ok: false, fehler: '', abgebrochen: true } : { ok: false, fehler: browserFehler(e) };
  }
  const ende = await aufruf('anmelden_ende', { antwort });
  return ende.ok ? { ok: true } : { ok: false, fehler: ende.fehler ?? t('Die Bestätigung ist fehlgeschlagen.') };
}

export async function meinePasskeys(): Promise<Passkey[]> {
  const { data } = await supabase.rpc('meine_passkeys');
  return (data as Passkey[] | null) ?? [];
}

export async function passkeyEntfernen(id: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('passkey_entfernen', { p_id: id });
  return !error && data === true;
}
